import { useEffect } from 'react';
import type { MouseEvent } from 'react';
import { SETTINGS_ROUTE } from './SettingsDrawerContext';
import './AppNav.css';

// AppNav — the persistent, app-level navigation rail (T-0150, canvas-home P1).
//
// The org canvas (/org) and the messaging console (/, /messages) used to read as
// two separate apps with no way between them (Erick: "it looks like there are 2
// different apps with no way to go from one to the other"). This slim fixed rail
// is present on BOTH surfaces so Canvas and Messages are always one click apart —
// the app reads as ONE app. It is deliberately layout-inert: a fixed-position rail
// plus a body class that pads the two surface roots (`.app-shell`, `.mock-window`)
// by the rail width, so neither surface's internal layout has to change.
//
// Routing here is the app's own lightweight path-based scheme (App.tsx) — there is
// no React Router — so navigation is a full-document `location.assign`.

type Dest = { key: string; label: string; glyph: string; href: string; active: (p: string) => boolean };

const DESTS: Dest[] = [
  {
    key: 'canvas',
    label: 'Canvas',
    glyph: '◆',
    href: '/org',
    active: (p) => p.startsWith('/org') || p === '/app' || p === '/mesh',
  },
  {
    key: 'messages',
    label: 'Messages',
    glyph: '✉',
    href: '/messages',
    active: (p) => p === '/messages' || p === '/',
  },
  // T-0167 — altitude 3. The LAYOUT LAW reserves the left nav for "where am I", and
  // names this destination outright: the fleet Schedules view is MAIN-VIEW content
  // reached from here, never an inspector. `↻` is the Schedule glyph everywhere else.
  {
    key: 'schedules',
    label: 'Schedules',
    glyph: '↻',
    href: '/schedules',
    active: (p) => p.startsWith('/schedules'),
  },
];

export function AppNav() {
  // Pad the two surface roots so their content clears the fixed rail. Toggled via a
  // body class (rather than per-surface edits) so mounting the rail never disturbs a
  // surface's own layout. Cleaned up on unmount.
  useEffect(() => {
    document.body.classList.add('app-has-rail');
    return () => document.body.classList.remove('app-has-rail');
  }, []);

  const pathname = window.location.pathname;

  // Same-document nav via the app's path router (there is no React Router — App.tsx
  // routes on `location.pathname`). Let modified clicks (new tab, copy link, etc.) and
  // the already-active item behave natively so the rail is a real, linkable nav.
  const navigate = (href: string, isActive: boolean) => (e: MouseEvent) => {
    if (isActive || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    window.location.assign(href);
  };

  const settingsActive = pathname.startsWith(SETTINGS_ROUTE);

  return (
    <nav className="app-nav-rail" aria-label="Primary">
      <span className="app-nav-mark" aria-hidden="true">
        🕸
      </span>
      <ul className="app-nav-list">
        {DESTS.map((d) => {
          const isActive = d.active(pathname);
          return (
            <li key={d.key}>
              <a
                className={isActive ? 'app-nav-item active' : 'app-nav-item'}
                href={d.href}
                aria-current={isActive ? 'page' : undefined}
                onClick={navigate(d.href, isActive)}
              >
                <span className="app-nav-glyph" aria-hidden="true">
                  {d.glyph}
                </span>
                <span className="app-nav-label">{d.label}</span>
              </a>
            </li>
          );
        })}
        {/* T-0221 (packet 007, D21) — Settings is a real destination now: it has a URL
            (`/settings`) that opens the drawer state (App.tsx + SettingsDrawerContext),
            so it is deep-linkable, refresh-safe, and back-button honest like every other
            rail item — no longer a drawer-toggling button. On mobile the rail is the
            bottom tab bar, so this is the 4th tab. It sits in the tail (rail bottom). */}
        <li className="app-nav-tail">
          <a
            className={settingsActive ? 'app-nav-item active' : 'app-nav-item'}
            href={SETTINGS_ROUTE}
            aria-current={settingsActive ? 'page' : undefined}
            onClick={navigate(SETTINGS_ROUTE, settingsActive)}
          >
            <span className="app-nav-glyph" aria-hidden="true">
              ⚙
            </span>
            <span className="app-nav-label">Settings</span>
          </a>
        </li>
      </ul>
    </nav>
  );
}
